import { Router } from "@angular/router";
import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AppConfig } from "../../../app.config";
import { DetalhesAssinaturaResponse } from "../arguments/assinatura/DetalhesAssinaturaResponse";
import {
  firstValueFrom,
  interval,
  Observable,
  Subject,
  Subscription,
} from "rxjs";
import { ItemLookupPlano } from "../../auth/models/item-lookup-plano.model";
import { CadastroUsuarioPlanoRequest } from "../../auth/models/CadastroUsuarioPlanoRequest.model";
import { ErrorHandlerService } from "src/app/services/errorHandler.service";
import { AuthService } from "./auth.service";

@Injectable()
export class AssinaturaService implements OnDestroy {
  private trialExpirou = false;
  private trialExpirou$ = new Subject<boolean>();

  private planoplanoAtual!: DetalhesAssinaturaResponse;
  private planoplanoAtual$ = new Subject<DetalhesAssinaturaResponse>();

  private intervaloAtualizacaoPlano$: Subscription; // 30 * 60 * 1000 Intervalo de 30 minutos (em milissegundos)

  private amodireito = false;

  private novaConsultaPlano: any = null;
  private planoConsultado = false;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private auth: AuthService
  ) {
    this.amodireito = this.router.url.indexOf("amoodireito") >= 0;

    this.intervaloAtualizacaoPlano$ = interval(30 * 60 * 1000).subscribe(() => {
      this.carregarPlanoAtual();
    });
  }

  ngOnDestroy(): void {
    this.intervaloAtualizacaoPlano$.unsubscribe();
  }

  public atualizaPlanoAtual() {
    this.carregarPlanoAtual();
  }

  public carregarPlanoAtual(): void {
    const url = `${AppConfig.apiEndpoint}/assinatura/meu-plano`;

    this.httpClient.get<any>(url).subscribe({
      next: (result) => {
        const resultado = result[0];

        if (resultado) {
          if (resultado?.trialGratuito) {
            resultado.diasTesteUtilizados =
              resultado?.tempoAvaliacaoGratuita -
              resultado?.diasRestantesAvaliacao;

            if (resultado?.tempoAvaliacaoGratuita <= 0) {
              this.trialExpirou = true;
              this.updateTrialExpirou();
            }
          }
        }
        this.planoplanoAtual = resultado;
        this.planoConsultado = true;
        this.updateplanoplanoAtual();
      },
      error: (error) => {
        this.errorHandlerService.handleError(error);
        // Falha na consulta (rede, 500, token vencido) não significa "sem
        // plano": mantém o último estado conhecido e consulta de novo em
        // instantes. Antes virava "sem plano" e o app parava de chamar a
        // API em silêncio.
        this.agendarNovaConsultaPlano();
      },
    });
  }

  private agendarNovaConsultaPlano(): void {
    if (this.novaConsultaPlano) {
      return;
    }

    this.novaConsultaPlano = setTimeout(() => {
      this.novaConsultaPlano = null;

      if (this.auth.userIsLoggedIn()) {
        this.carregarPlanoAtual();
      }
    }, 60 * 1000);
  }

  // Regra única de "sem assinatura válida não usa a plataforma", usada pela
  // tela de assinatura e pelo interceptor que bloqueia a API:
  //  - a API respondeu e o usuário não tem assinatura do SVM
  //  - avaliação gratuita vencida (ou sem data de término)
  //  - plano pago com problema de pagamento
  // Só vale depois da primeira consulta do plano: antes disso (ex.: durante
  // o login) nada é bloqueado, senão o usuário nem chegaria à tela de pagar.
  public acessoBloqueado(): boolean {
    if (!this.auth.userIsLoggedIn()) {
      // Saiu da conta: o plano em memória era do usuário anterior
      this.planoConsultado = false;
      this.planoplanoAtual = undefined;
      return false;
    }

    if (!this.planoConsultado) {
      return false;
    }

    const plano = this.planoplanoAtual;

    if (!plano?.produto) {
      return true;
    }

    if (plano.trialGratuito) {
      const diasRestantes = Math.floor(plano.diasRestantesAvaliacao);
      return isNaN(diasRestantes) || diasRestantes <= 0;
    }

    return this.auth.getAccessToken().problemaPagamento === true;
  }

  public listarPlanos(apenasTrial: boolean): ItemLookupPlano[] {
    const planos: ItemLookupPlano[] = [
      { id: 6, descricao: "Teste grátis", periodicidade: "trial" },
      {
        id: 7,
        descricao: this.amodireito
          ? "Cobrança mensal - R$ 21,00 (15% off)"
          : "Cobrança mensal - R$ 24,90",
        periodicidade: "mensal",
      },
      {
        id: 8,
        descricao: this.amodireito
          ? "Cobrança anual - R$ 174,30 (30% off)"
          : "Cobrança anual - R$ 249,00",
        periodicidade: "anual",
      },
    ];

    return planos.filter(
      (p) =>
        (apenasTrial && p.periodicidade === "trial") ||
        p.periodicidade !== "trial"
    );
  }

  // Devolvem a chamada para a tela esperar o resultado — antes eram
  // disparadas sem retorno e a tela mostrava sucesso mesmo com o pagamento
  // recusado
  public assinarPlano(payload: CadastroUsuarioPlanoRequest): Promise<any> {
    const url = `${AppConfig.apiEndpoint}/assinatura/novo`;

    return firstValueFrom(this.httpClient.post(url, payload));
  }

  public alterarPlano(payload: CadastroUsuarioPlanoRequest): Promise<any> {
    const url = `${AppConfig.apiEndpoint}/assinatura/alterar`;

    return firstValueFrom(this.httpClient.post(url, payload));
  }

  public updateTrialExpirou() {
    this.trialExpirou$.next(this.trialExpirou);
  }

  public getTrialExpirou(): Observable<boolean> {
    return this.trialExpirou$.asObservable();
  }

  public updateplanoplanoAtual() {
    this.planoplanoAtual$.next(this.planoplanoAtual);
  }

  public getplanoplanoAtual(): Observable<DetalhesAssinaturaResponse> {
    return this.planoplanoAtual$.asObservable();
  }
}
